import { Injectable } from '@angular/core';
import { BucketStorageService, HttpResponseException, InputAppDataService } from '@monorepo/services';
import {
  TImageDBLockResult,
  TImageDBLockedBy,
  TSyncedImagesBucketLockedBy,
  TSyncedImagesBucketAddtionLockResult,
  TImagesBucket,
  TBucketSyncStatus,
  TServerImagesBucketLockedBy,
  TServerImagesBucketAddtionLockResult,
  TDefectiveFromServer,
  TSingleDefectiveEvent,
  TInitStoreLockedBy,
  TRemoteDeploymentCheck,
  TRemoteDeploymentCheckWithImageSyncResult,
  TBgImageSyncResult,
  TUserAction,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import produce from 'immer';
import { TBucketId, TBucketInfo } from 'libs/services/src/lib/capacitor/bucket-storage.types';
import { from, lastValueFrom, Observable, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { getConfigurations, getAppIdentifier } from '../app-master-db/app-master.query';
import { TInputAppState } from '../reducer';
import {
  AcquireAllLocksAndInitImageStore,
  AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock,
  AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock,
  AcquireLockAndBackfillMissingServerImagesBucketList,
  AcquireLockAndThenSyncImagesBucketAndThenReleaseLock,
  ReleaseImageMetadataDBLock,
  UpdateImageDBLockDetails,
  UpdateServerImagesBucketDBLockDetails,
  UpdateSyncedImagesBucketDBLockDetails,
} from './image-db.actions';
import {
  getImageDBLockDetailsAndNextCacheResetTime,
  getAllImagesBucketDBs,
  getServerImagesBucketDBLockDetailsAndNextCacheResetTime,
  getSyncedImagesBucketDBLockDetailsAndNextCacheResetTime,
  getAllDBsLockDetails,
} from './image-db.query';
import * as _ from 'lodash';
import { PreviewImagesComponent } from 'libs/services/src/lib/capacitor/preview-images/preview-images.component';
import { InputAppErrorDisplay } from '@monorepo/utils';
import { ModalController, Platform } from '@ionic/angular';
import { getOnlineStatus } from '../metadata/metadata.query';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class InputAppImageDBFacade {
  private appUpdateSubscription = new Subject<TRemoteDeploymentCheckWithImageSyncResult>();
  constructor(
    private store: Store<TInputAppState>,
    private bucketStorageService: BucketStorageService,
    private inputAppDataService: InputAppDataService,
    private inputAppErrorDisplay: InputAppErrorDisplay,
    private modalController: ModalController,
    private platform: Platform,
    private translate: TranslateService,
    private inputAppErrorDisplayUtil: InputAppErrorDisplay,
  ) {}

  getAppUpdateSubscription() {
    return this.appUpdateSubscription.asObservable();
  }

  emitAppUpdateSubscription(remoteDeploymentCheckWithSyncResult: TRemoteDeploymentCheckWithImageSyncResult) {
    this.appUpdateSubscription.next(remoteDeploymentCheckWithSyncResult);
  }

  getImageDBLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getImageDBLockDetailsAndNextCacheResetTime));
  }

  getOnlineStatus() {
    return this.store.pipe(select(getOnlineStatus));
  }

  getSyncedImagesBucketDBLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getSyncedImagesBucketDBLockDetailsAndNextCacheResetTime));
  }

  getServerImagesBucketDBLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getServerImagesBucketDBLockDetailsAndNextCacheResetTime));
  }

  getAllDBsLockDetails() {
    return this.store.pipe(select(getAllDBsLockDetails));
  }

  isImagesDBsUnlocked() {
    return this.getAllDBsLockDetails().pipe(
      take(1),
      map(
        ({ userDBLockDetails, bgDbLockDetails, serverImageDBLockDetails }) =>
          !userDBLockDetails.isLocked && !bgDbLockDetails.isLocked && !serverImageDBLockDetails.isLocked,
      ),
    );
  }

  acquireAllLocksAndInitStore(initBy: TInitStoreLockedBy) {
    this.store.dispatch(new AcquireAllLocksAndInitImageStore(initBy));
  }

  acquireLockForImagesBucketAddition(lockFor: TImageDBLockedBy): Observable<TImageDBLockResult> {
    return this.getImageDBLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'PRE_INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('input_app_image_db_effects.factoryEndTime'),
          };
        } else {
          this.store.dispatch(
            new UpdateImageDBLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  acquireLockForSyncedImagesBucketIdsAddition(
    lockFor: TSyncedImagesBucketLockedBy,
  ): Observable<TSyncedImagesBucketAddtionLockResult> {
    return this.getSyncedImagesBucketDBLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'PRE_INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('input_app_image_db_effects.factoryEndTime'),
          };
        } else {
          this.store.dispatch(
            new UpdateSyncedImagesBucketDBLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  acquireLockForServerImagesBucketIdsAddition(
    lockFor: TServerImagesBucketLockedBy,
  ): Observable<TServerImagesBucketAddtionLockResult> {
    return this.getServerImagesBucketDBLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'PRE_INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('input_app_image_db_effects.factoryEndTime'),
          };
        } else {
          this.store.dispatch(
            new UpdateServerImagesBucketDBLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  getBucketIdFromUniqueEventId(commitId: string): TBucketId {
    return `BUCKET_ID_${commitId}`;
  }

  addImagesBucketAndThenCommit(commitId: TBucketId, bucketInfo: TBucketInfo, eventTime: number) {
    this.store.dispatch(new AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock(bucketInfo, commitId, eventTime));
  }

  addImagesBucketOnBulk(bucketIds: TBucketId[], eventTime: number) {
    this.store.dispatch(new AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock(bucketIds, eventTime));
  }

  async uploadBucket(bucketId: TBucketId): Promise<TBucketSyncStatus> {
    const files = await this.bucketStorageService.getAllDefectiveFilesByBucketId(bucketId);
    try {
      const formData = new FormData();
      formData.append('id', bucketId);
      formData.append('imageCount', files.length.toString());
      const response = await Promise.all(
        files.map(async (file) => {
          try {
            const uploadFile = await this.bucketStorageService.loadFileData(file);
            const response = await fetch(uploadFile.data);
            const blob = await response.blob();
            return {
              status: true,
              blob,
              file,
            } as {
              status: true;
              blob: Blob;
              file: TBucketInfo['files'][number];
            };
          } catch (error) {
            return {
              status: false,
              error,
            } as {
              status: false;
              error: any;
            };
          }
        }),
      );
      if (response.some((responseInfo) => !responseInfo.status)) {
        return 'UNABLE_TO_GET_ALL_FILES';
      } else {
        response.forEach((responseInfo, index) => {
          if (responseInfo.status) formData.append(`image.${index}`, responseInfo.blob, responseInfo.file.name);
        });
        const uploadResponse = await lastValueFrom(this.inputAppDataService.uploadPhotos(formData));
        try {
          if (uploadResponse.result === 'Success') {
            return 'SYNCED';
          } else {
            return 'SYNCED_WITH_FAILURE';
          }
        } catch (error) {
          const status = (error as HttpResponseException).httpResponse.status;
          return status === 0 ? 'NO_INTERNET' : 'SERVER_ERROR';
        }
      }
    } catch (err) {
      return 'UNKNOWN_ERROR';
    }
  }

  async uploadAllBuckets(bucketIds: TBucketId[]) {
    const response: Record<TBucketId, TBucketSyncStatus> = {};
    for (let bucketId of bucketIds) {
      const individualResponse = await this.uploadBucket(bucketId);
      response[bucketId] = individualResponse;
    }
    return response;
  }

  aqcuireLockAndBackfillMissingServerImagesBuckets() {
    this.store.dispatch(new AcquireLockAndBackfillMissingServerImagesBucketList());
  }

  backfillImagesByBucketIds(toBeAddedBucketIdsWithoutFiles: TImagesBucket[]) {
    const groupedBucketInfo = _.groupBy(toBeAddedBucketIdsWithoutFiles, (bucketInfo) => bucketInfo.bucketId);
    return this.inputAppDataService.getImageUrlsByIds({ imageIds: Object.keys(groupedBucketInfo) as TBucketId[] }).pipe(
      take(1),
      switchMap((response) => {
        const mappedInfo = Object.entries(response.urlMap).map(([bucketId, urls]) => {
          return {
            bucketId: bucketId as TBucketId,
            urls,
          };
        });
        return from(this.bucketStorageService.addImagesBucketListForServerDefectives(mappedInfo)).pipe(
          take(1),
          map((res) => {
            return toBeAddedBucketIdsWithoutFiles.reduce((acc, curr) => {
              if (res[curr.bucketId]) {
                acc.push(
                  produce(curr, (draft) => {
                    draft.files = res[curr.bucketId].map((url) => {
                      return {
                        publicPath: url,
                      };
                    });
                  }),
                );
              }
              return acc;
            }, [] as TImagesBucket[]);
          }),
        );
      }),
    );
  }

  getConfigurations() {
    return this.store.pipe(select(getConfigurations));
  }

  getAppIdentifier() {
    return this.store.pipe(select(getAppIdentifier));
  }

  pollForImageMetadataDBLock(userAction: TUserAction, hasLoaderShown = false) {
    this.acquireLockForSyncedImagesBucketIdsAddition('BACKGROUND_IMAGE_SYNCING')
      .pipe(take(1))
      .subscribe(async (bgDBLockDetails) => {
        if (bgDBLockDetails.status === 'SUCCESS') {
          if (hasLoaderShown) await this.inputAppErrorDisplayUtil.dismissLoading();
          this.store.dispatch(new ReleaseImageMetadataDBLock());
          if (userAction.chainWithAppUpdate) {
            this.forceSyncImagesAndUpdateApp(userAction.chainWithAppUpdate, true);
          }
        } else {
          if (!hasLoaderShown)
            await this.inputAppErrorDisplayUtil.presentLoading('Evaluating pending defective images...');
          setTimeout(() => {
            this.pollForImageMetadataDBLock(userAction, true);
          }, 2000);
        }
      });
  }

  acquireLockAndSyncImages() {
    this.getOnlineStatus()
      .pipe(take(1))
      .subscribe((isOnline) => {
        if (isOnline) {
          this.store.dispatch(new AcquireLockAndThenSyncImagesBucketAndThenReleaseLock({}));
        }
      });
  }

  forceSyncImagesAndUpdateApp(remoteDeploymentCheck: TRemoteDeploymentCheck, isBgDBUnlocked: boolean) {
    this.getOnlineStatus()
      .pipe(take(1))
      .subscribe((isOnline) => {
        if (!isOnline) {
          this.emitAppUpdateSubscription({
            remoteDeploymentCheck,
            syncResult: {
              status: 'NO_INTERNET_FOR_SYNCING_PENDING_DEFECTIVE_IMAGES',
            },
          });
        } else if (!isBgDBUnlocked) {
          this.pollForImageMetadataDBLock({ chainWithAppUpdate: remoteDeploymentCheck }, false);
        } else {
          this.store.dispatch(
            new AcquireLockAndThenSyncImagesBucketAndThenReleaseLock({ appUpdate: remoteDeploymentCheck }),
          );
        }
      });
  }

  getAllImagesBucketDBs() {
    return this.store.pipe(select(getAllImagesBucketDBs));
  }

  getPublicPathsByBucketId(
    bucketId: TBucketId,
    actionType: TDefectiveFromServer['actionType'] | TSingleDefectiveEvent['actionType'],
  ) {
    return this.getAllImagesBucketDBs().pipe(
      take(1),
      map(({ serverImagesBucketDB, imagesBucketDB }) => {
        const bucketList =
          actionType === 'DEFECTIVE' ? imagesBucketDB.imageBuckets.list : serverImagesBucketDB.imageBuckets.list;
        let bucketInfo = bucketList.find((bucketInfo) => bucketInfo.bucketId === bucketId);
        if (bucketInfo) {
          return {
            status: true,
            publicPaths: bucketInfo.files.map((fileInfo) => fileInfo.publicPath),
          };
        } else
          return {
            status: false,
            publicPaths: [],
          };
      }),
    );
  }

  previewImageByBucketId(
    bucketId: TBucketId,
    actionType: TDefectiveFromServer['actionType'] | TSingleDefectiveEvent['actionType'],
  ) {
    this.getPublicPathsByBucketId(bucketId, actionType)
      .pipe(take(1))
      .subscribe(async (res) => {
        if (res.status) {
          const popup = await this.modalController.create({
            component: PreviewImagesComponent,
            componentProps: {
              publicPaths: res.publicPaths,
            },
            showBackdrop: true,
            cssClass: 'defective-flow-container-modal',
          });
          await popup.present();
        } else {
          this.inputAppErrorDisplay.showPopup(
            this.translate.instant('input_app_common.error'),
            this.translate.instant('input_app_image_db_facade.unableToFetchImages'),
          );
        }
      });
  }

  isImageCapturingEnabled() {
    return this.getAppIdentifier().pipe(
      take(1),
      map((app_identifier) => {
        return (
          (this.platform.is('android') || this.platform.is('ios')) &&
          app_identifier &&
          (app_identifier === 'bundle_qc_v2' ||
            app_identifier === 'endline_qc_v2' ||
            app_identifier === 'per_piece_qc_v2' ||
            app_identifier === 'fabric_inspection' ||
            app_identifier === 'aql_v2' ||
            app_identifier === 'roaming_qc_v2')
        );
      }),
    );
  }

  getCurrentImageSyncResult(): Observable<TBgImageSyncResult> {
    return this.getAllImagesBucketDBs().pipe(
      take(1),
      map(({ imagesBucketDB, imagesBucketMetadataDB }) => {
        if (
          imagesBucketDB.imageBuckets.list.some(
            (imageBucket) => !imagesBucketMetadataDB.bucketsInfo[imageBucket.bucketId],
          ) ||
          Object.values(imagesBucketMetadataDB.bucketsInfo).some(
            (imageBucket) => imageBucket.status === 'YET_TO_BE_SYNCED',
          )
        ) {
          return {
            status: 'PENDING_DEFECTIVE_IMAGES_SYNC_FAILED',
          };
        } else {
          return {
            status: 'ALL_SYNCED',
          };
        }
      }),
    );
  }

  updateAppAfterImageSync(remoteDeploymentCheck: TRemoteDeploymentCheck) {
    return this.getCurrentImageSyncResult()
      .pipe(take(1))
      .subscribe((syncResult) => {
        this.emitAppUpdateSubscription({
          remoteDeploymentCheck,
          syncResult,
        });
      });
  }

  getValidImageId(data: TBucketInfo | undefined) {
    if (data && typeof data !== 'string' && this.bucketStorageService.isBucketValid(data)) {
      return data.id;
    }
    return null;
  }
}
